import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { DEFAULT_FILTERS, RANGE_PRESETS, parseDateKey, parseStatesParam, rangeParamsFor, statesParam, withStates } from './dashboardFilters'

const dateKeyParam = (value) => (parseDateKey(value) ? value : '')

function readFilters(searchParams) {
  const preset = searchParams.get('range')
  return {
    ...DEFAULT_FILTERS,
    preset: RANGE_PRESETS.some((item) => item.key === preset) ? preset : DEFAULT_FILTERS.preset,
    // A shared link with a malformed date reads as that date not picked yet,
    // rather than a range nothing can draw.
    customStart: dateKeyParam(searchParams.get('from')),
    customEnd: dateKeyParam(searchParams.get('to')),
    compare: searchParams.get('compare') !== 'off',
    states: parseStatesParam(searchParams.get('states')),
  }
}

// The dashboard filters live in the URL, so a link carries the range, the
// comparison and the states with it and every widget reads one filters
// object. `filters` is
// recomputed only when the URL changes, so the range end (now) stays stable
// between renders and query keys do not churn. `updateParams` is the shared
// writer for anything else the shell keeps in the URL, such as the selected
// dashboard.
export function useDashboardFilters() {
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => {
    const input = readFilters(searchParams)
    return { ...input, range: withStates(rangeParamsFor(input), input.states) }
  }, [searchParams])

  const updateParams = useCallback((mutate) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      mutate(next)
      return next
    }, { replace: true })
  }, [setSearchParams])

  const changeFilters = useCallback((patch) => {
    updateParams((next) => {
      if (patch.preset !== undefined) {
        next.set('range', patch.preset)
        if (patch.preset !== 'custom') { next.delete('from'); next.delete('to') }
      }
      if (patch.customStart !== undefined) next.set('from', patch.customStart)
      if (patch.customEnd !== undefined) next.set('to', patch.customEnd)
      if (patch.compare !== undefined) {
        if (patch.compare) next.delete('compare')
        else next.set('compare', 'off')
      }
      if (patch.states !== undefined) {
        if (patch.states.length) next.set('states', statesParam(patch.states))
        else next.delete('states')
      }
    })
  }, [updateParams])

  return { filters, changeFilters, updateParams, searchParams }
}
